import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { Box } from '../../box/Box';
import type { ElementProps } from '../../element/Element';
import { Element } from '../../element/Element';
import { Elevation } from '../../elevation/Elevation';
import type { TimelineOrientation } from '../Timeline.types';
import styles from './Timeline.Memory.module.scss';

export interface Props extends ElementProps {}

const MemoryContainer = (
  props: Props & {
    offsetPx?: number;
    orientation: TimelineOrientation;
    /** Mute the memory to 40% opacity, grayscale */
    ignored?: boolean;
    /** Indicate "indirectly logged", 50% opacity */
    subtle?: boolean;
  }
) => {
  const { offsetPx, orientation, ignored, subtle, style, ...rest } = props;
  return (
    <Element
      {...rest}
      style={{
        top: orientation === 'vertical' ? offsetPx : undefined,
        left: orientation === 'horizontal' ? offsetPx : undefined,
        ...style,
      }}
      className={classNames(
        styles.memoryContainer,
        { [styles.ignored]: ignored, [styles.subtle]: subtle },
        props.className
      )}
    />
  );
};
MemoryContainer.displayName = 'Timeline.Memory';

const MemoryInline = (props: Props) => {
  return (
    <Box.Column gap="small" {...props} className={classNames(props.className, styles.memoryInline)}>
      {props.children}
    </Box.Column>
  );
};
MemoryInline.displayName = 'Timeline.Memory.Inline';

const MemoryPopover = (props: Props & { isOpen: boolean; offsetX?: number }) => {
  const { isOpen, offsetX = 20, ...rest } = props;
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="memory-popover"
          className={styles.memoryPopover}
          animate={{ opacity: 1, y: -9, x: offsetX }}
          initial={{ opacity: 0.1, y: -9, x: offsetX }}
          exit={{ opacity: 0, y: -9, x: offsetX }}
          transition={{ type: 'spring', duration: 0.2 }}
        >
          <Elevation
            as={Box.Column}
            gap="medium"
            size="medium"
            depth="medium"
            {...rest}
            className={classNames(props.className, styles.memoryPopover)}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
MemoryPopover.displayName = 'Timeline.Memory.Popover';

const MemoryPopoverSheet = (props: Props & { isOpen?: boolean }) => {
  const { isOpen, ...rest } = props;
  if (!isOpen) return null;
  return (
    <motion.div
      key="memory-popover-details"
      className={styles.memoryPopoverDetails}
      animate={{ opacity: 1, left: 0, top: 4 }}
      initial={{ opacity: 0, left: 0, top: 8 }}
      exit={{ opacity: 0, left: 0, top: 8 }}
      transition={{ type: 'spring', duration: 0.3 }}
      onClick={e => e.stopPropagation()}
    >
      <Elevation
        as={Box.Column}
        depth="high"
        {...rest}
        className={classNames(styles.memoryPopoverDetails, props.className)}
      />
    </motion.div>
  );
};
MemoryPopoverSheet.displayName = 'Timeline.Memory.Popover.Sheet';

type BarProps = Props & {
  /** Pass in something that renders a plain `img` tag */
  children: React.ReactElement;
  /** Height of the bar in `px` */
  sizePx: number;
  /** Orientation of bar on timeline */
  orientation: TimelineOrientation;
  /** `style` compatible color designation for this bar */
  color?: string;
};
const Bar = (props: BarProps) => {
  const { sizePx, orientation, color = 'rgba(25,210,253,0.30)', style, ...rest } = props;

  return (
    <Element
      {...rest}
      style={{
        backgroundColor: color,
        height: orientation === 'vertical' ? `${sizePx}px` : undefined,
        width: orientation === 'horizontal' ? `${sizePx}px` : undefined,
        ...style,
      }}
      className={classNames(styles.bar, props.className, 'fs-exclude')}
    />
  );
};

Bar.displayName = 'Timeline.Memory.Bar';

/**
 * Wraps the Memory Item on Timeline
 * @see Timeline.tsx where this is re-exported
 */
export const TimelineMemory = Object.assign(MemoryContainer, {
  /** Inline display of the Memory */
  Inline: MemoryInline,
  /** Memory/Duration Bar, indicating length of Memory */
  Bar,
  /** Hover-the-Memory style popover */
  Popover: Object.assign(MemoryPopover, {
    /** Display extra details from within Memory popover */
    Sheet: MemoryPopoverSheet,
  }),
});

TimelineMemory.displayName = 'Timeline.Memory';
TimelineMemory.Inline.displayName = 'Timeline.Memory.Inline';
TimelineMemory.Bar.displayName = 'Timeline.Memory.Bar';
TimelineMemory.Popover.displayName = 'Timeline.Memory.Popover';
TimelineMemory.Popover.Sheet.displayName = 'Timeline.Memory.Popover.Sheet';
